<template>
  <v-dialog v-model="isVisible" max-width="1000px" persistent>
    <v-card>
      <v-card-title class="popup-header">
        <span class="headline white-text">Historial de Fichajes por Empleado</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closePopup" :disabled="loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="popup-content">
        <!-- Vista de lista de empleados -->
        <div v-if="!selectedEmployee" class="employees-list-view">
          <p class="subtitle-text">
            Selecciona un empleado para ver su historial de fichajes.
          </p>

          <div v-if="loadingUsers" class="text-center my-6">
            <v-progress-circular indeterminate color="primary" size="40" />
            <p class="mt-2">Cargando empleados...</p>
          </div>

          <div v-else class="employees-grid">
            <div
              v-for="user in users"
              :key="user.id"
              class="employee-card"
              @click="selectEmployee(user)"
            >
              <v-icon class="employee-icon" color="primary">mdi-account-circle</v-icon>
              <div class="employee-info">
                <div class="employee-name">{{ user.username }}</div>
                <div class="employee-email">{{ user.email }}</div>
                <div class="employee-role">{{ getFriendshipKindDisplay(user.friendship_kind) }}</div>
              </div>
              <v-icon class="arrow-icon">mdi-chevron-right</v-icon>
            </div>
          </div>
        </div>

        <!-- Vista de historial del empleado seleccionado -->
        <div v-else class="employee-history-view">
          <div class="history-header">
            <v-btn icon @click="backToList" class="back-btn">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <div class="selected-employee-info">
              <span class="employee-name">{{ selectedEmployee.username }}</span>
              <span class="employee-email">{{ selectedEmployee.email }}</span>
            </div>
          </div>

          <!-- Filtro de fechas -->
          <div class="filter-section">
            <div class="filter-row">
              <label class="filter-label">Desde:</label>
              <input type="date" v-model="filter.startDate" class="date-input" />
              <label class="filter-label">Hasta:</label>
              <input type="date" v-model="filter.endDate" class="date-input" />
              <v-btn small color="primary" @click="loadEmployeeClocks" :disabled="loading">
                Filtrar
              </v-btn>
              <v-btn small text @click="clearFilter">
                Limpiar
              </v-btn>
            </div>
          </div>

          <div v-if="loading" class="text-center my-6">
            <v-progress-circular indeterminate color="primary" size="40" />
            <p class="mt-2">Cargando fichajes...</p>
          </div>

          <div v-else-if="employeeClocks.length === 0" class="no-data">
            <v-icon large color="grey">mdi-clock-outline</v-icon>
            <p>No hay fichajes registrados para este empleado en el rango seleccionado.</p>
          </div>

          <div v-else class="clocks-list">
            <div
              v-for="clock in employeeClocks"
              :key="clock.uuid"
              class="clock-item"
            >
              <div class="clock-info">
                <div class="clock-date">{{ formatDate(clock.timestamp) }}</div>
                <div class="clock-type">{{ formatType(clock.type) }}</div>
              </div>
              <div class="clock-actions">
                <v-chip
                  :color="getTypeColor(clock.type)"
                  text-color="white"
                  small
                >
                  {{ formatType(clock.type) }}
                </v-chip>
                <v-btn
                  small
                  outlined
                  color="primary"
                  @click="openEditClock(clock)"
                  class="ml-2"
                >
                  <v-icon small left>mdi-pencil</v-icon>
                  Modificar
                </v-btn>
              </div>
            </div>
          </div>

          <!-- Paginaci\u00f3n -->
          <div v-if="employeeClocks.length > 0" class="pagination-section">
            <span class="pagination-info">
              Mostrando {{ employeeClocks.length }} fichajes
            </span>
          </div>
        </div>
      </v-card-text>

      

      <!-- Snackbar -->
      <v-snackbar
        v-model="snackbar.visible"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
      >
        <b>{{ snackbar.text }}</b>
      </v-snackbar>
    </v-card>

    <!-- Popup de edición de fichaje -->
    <update-clock-popup
      v-if="editClockPopup.show && editClockPopup.clock"
      :show="editClockPopup.show"
      :clock="editClockPopup.clock"
      :errors="editClockPopup.errors"
      :is-admin="true"
      @close="handleEditClockClose"
    />
  </v-dialog>
</template>

<script>
import { APIClockRepository } from './APIClockRepository';
import { formatTimestamp, formatClockType } from '@/modules/working_hours/domain/Utils';
import UpdateClockPopup from './UpdateClockPopup.vue';
import { Clock } from '@/modules/working_hours/domain/Clock';

export default {
  name: 'EmployeeHistoryPopup',
  components: {
    UpdateClockPopup
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    users: {
      type: Array,
      default: () => []
    },
    companydbId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isVisible: false,
      loading: false,
      loadingUsers: false,
      selectedEmployee: null,
      employeeClocks: [],
      filter: {
        startDate: '',
        endDate: ''
      },
      clockRepo: null,
      snackbar: {
        visible: false,
        text: '',
        color: 'error',
        timeout: 3000
      },
      editClockPopup: {
        show: false,
        clock: null,
        errors: {}
      }
    };
  },
  created() {
    this.clockRepo = new APIClockRepository();
    this.setDefaultDateFilter();
  },
  watch: {
    visible(newValue) {
      this.isVisible = newValue;
      if (newValue) {
        this.selectedEmployee = null;
        this.employeeClocks = [];
        this.setDefaultDateFilter();
      }
    },
    isVisible(newValue) {
      if (!newValue) {
        this.$emit('close');
      }
    }
  },
  methods: {
    setDefaultDateFilter() {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      this.filter.startDate = firstDayOfMonth.toISOString().split('T')[0];
      this.filter.endDate = today.toISOString().split('T')[0];
    },

    showSnackbar(color, text) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.visible = true;
    },

    getFriendshipKindDisplay(kind) {
      const kindMap = {
        'propietario': 'Propietario',
        'admin': 'Administrador',
        'empleado': 'Empleado',
        'cliente': 'Cliente',
      };
      return kindMap[kind] || kind;
    },

    selectEmployee(user) {
      this.selectedEmployee = user;
      this.loadEmployeeClocks();
    },

    backToList() {
      this.selectedEmployee = null;
      this.employeeClocks = [];
    },

    async loadEmployeeClocks() {
      if (!this.selectedEmployee) return;

      this.loading = true;
      try {
        const criteria = [
          ['user_id', '==', this.selectedEmployee.id]
        ];

        // Agregar filtros de fecha si est\u00e1n definidos
        if (this.filter.startDate) {
          const startDate = new Date(this.filter.startDate);
          startDate.setHours(0, 0, 0, 0);
          criteria.push(['timestamp', '>=', startDate.toISOString()]);
        }
        if (this.filter.endDate) {
          const endDate = new Date(this.filter.endDate);
          endDate.setHours(23, 59, 59, 999);
          criteria.push(['timestamp', '<=', endDate.toISOString()]);
        }

        const clocks = await this.clockRepo.find_by_criteria(
          criteria,
          ['-timestamp'],
          100,
          0,
          true
        );

        this.employeeClocks = clocks;
      } catch (error) {
        console.error('Error loading employee clocks:', error);
        this.showSnackbar('error', 'Error al cargar los fichajes del empleado');
      } finally {
        this.loading = false;
      }
    },

    clearFilter() {
      this.setDefaultDateFilter();
      if (this.selectedEmployee) {
        this.loadEmployeeClocks();
      }
    },

    formatDate(timestamp) {
      return formatTimestamp(timestamp);
    },

    formatType(type) {
      return formatClockType(type);
    },

    getTypeColor(type) {
      const colors = {
        'in': 'green',
        'out': 'red',
        'pause': 'orange',
        'continue': 'blue'
      };
      return colors[type] || 'grey';
    },

    closePopup() {
      if (!this.loading) {
        this.isVisible = false;
        this.selectedEmployee = null;
        this.employeeClocks = [];
      }
    },

    openEditClock(clock) {
      this.editClockPopup.clock = clock;
      this.editClockPopup.errors = {};
      this.editClockPopup.show = true;
    },

    async handleEditClockClose(result) {
      this.editClockPopup.show = false;

      if (result && result.updated_clock) {
        // Validar justificación
        if (!result.justification || result.justification.trim() === '') {
          this.editClockPopup.errors = { justification: 'La justificación es obligatoria' };
          this.editClockPopup.show = true;
          return;
        }

        try {
          // Crear el clock actualizado con el nuevo cambio (validada porque es admin)
          const updatedClock = new Clock(
            result.updated_clock.uuid,
            result.updated_clock.timestamp,
            result.updated_clock.type,
            result.updated_clock.user,
            [
              ...(this.editClockPopup.clock.changes || []),
              {
                timestamp: result.updated_clock.timestamp,
                type: result.updated_clock.type,
                justification: result.justification,
                state: 'validada'
              }
            ],
            false
          );

          await this.clockRepo.save(updatedClock, {
            companydb_id: this.companydbId,
            is_admin_or_owner: true
          });

          this.showSnackbar('success', 'Fichaje modificado correctamente');

          // Recargar los fichajes empleado
          await this.loadEmployeeClocks();
        } catch (error) {
          console.error('Error al modificar el fichaje:', error);
          this.showSnackbar('error', 'Error al modificar el fichaje');
        }
      }

      this.editClockPopup.clock = null;
      this.editClockPopup.errors = {};
    }
  }
};
</script>

<style scoped>
.popup-header {
  background-color: var(--Primary-700, #7339B7);
  color: white;
  padding: 16px 24px;
}

.popup-header .headline {
  color: white !important;
}

.popup-header .v-icon {
  color: white !important;
}

.popup-content {
  padding: 24px;
  min-height: 400px;
  max-height: 60vh;
  overflow-y: auto;
}

.subtitle-text {
  color: #666;
  margin-bottom: 24px;
  font-size: 14px;
}

.employees-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.employee-card {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.employee-card:hover {
  background: #f0f0f0;
  border-color: var(--Primary-700, #7339B7);
  transform: translateX(4px);
}

.employee-icon {
  font-size: 48px !important;
  margin-right: 16px;
}

.employee-info {
  flex: 1;
}

.employee-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.employee-email {
  font-size: 13px;
  color: #666;
}

.employee-role {
  font-size: 12px;
  color: #888;
  margin-top: 4px;
}

.arrow-icon {
  color: #999;
}

/* Vista de historial */
.employee-history-view {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.history-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.back-btn {
  background: #f0f0f0 !important;
}

.selected-employee-info {
  display: flex;
  flex-direction: column;
}

.selected-employee-info .employee-name {
  font-size: 18px;
  font-weight: 600;
}

.selected-employee-info .employee-email {
  font-size: 13px;
  color: #666;
}

.filter-section {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-label {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.date-input {
  padding: 8px 12px;
  border: 1px solid #D1D1D1;
  border-radius: 8px;
  font-size: 14px;
  background: white;
}

.clocks-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.clock-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.clock-info {
  display: flex;
  flex-direction: column;
}

.clock-date {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.clock-type {
  font-size: 12px;
  color: #666;
}

.clock-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #999;
}

.no-data p {
  margin-top: 16px;
  text-align: center;
}

.pagination-section {
  display: flex;
  justify-content: center;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.pagination-info {
  font-size: 13px;
  color: #666;
}

.popup-actions {
  padding: 16px 24px;
  background-color: #f5f5f5;
}

@media (max-width: 600px) {
  .popup-content {
    padding: 16px;
  }

  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .date-input {
    width: 100%;
  }
}
</style>
